// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../styled-components/dark';
import { type OutcomeStatus, OutcomeStatuses, type StatusFilter, StatusFilters } from '../../types/history';
import { getOutcomeColorFromStatus, getOutcomeIconFromStatus } from '../../utils/outcome';
import ExpandArrow from '../ExpandArrow/ExpandArrow';

const STATUS_SUMMARY_ORDER = [
  OutcomeStatuses.WON,
  OutcomeStatuses.CANCELLED,
  OutcomeStatuses.LIVE,
  OutcomeStatuses.RUNNING,
  OutcomeStatuses.LOST,
];

const Wrapper = styled.div`
  padding: 1em;
  padding-bottom: 0.33em;
`;

const LineWrapper = styled.div`
  display: flex;
  gap: 3px;

  height: 0.27em;
  border-radius: 0.2em;
  overflow: hidden;
`;

const Line = styled.div`
  height: 100%;

  ${({ $width, $color }) => css`
    background-color: ${$color};
    width: ${$width * 100}%;
  `}
`;

const OutcomeNbWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65em;

  margin-top: 0.7em;
`;

const ArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 2em;
  height: 2em;
  background: ${theme.History.betslip.outcome.arrowBackground};
  border-radius: 100%;

  cursor: pointer;
`;

const StyledExpandArrow = styled(ExpandArrow)`
  height: 0.52em;

  transition: transform 250ms ease-in-out;

  ${({ $isExpand }) =>
    $isExpand
      ? css`
          transform: rotate(0deg) translateY(-0.05em);
        `
      : css`
          transform: rotate(180deg) translateY(-0.05em);
        `}
`;

const OutcomeNb = styled.div`
  display: flex;
  align-items: center;

  border: 1px solid
    ${({ $selected }) => theme.History.betslip.outcome[$selected ? 'selectedBorderColor' : 'borderColor']};
  transition: border-color 200ms ease-in-out;
  border-radius: 1.87em;
  padding: 0.2em 0.62em;

  color: ${theme.History.betslip.outcome.color};
  font-size: 1.07em;

  cursor: pointer;
`;

const StatusIcon = styled.svg`
  width: 0.87em;
  height: 0.87em;
  margin-right: 0.31em;
  overflow: visible !important;
`;

type HistoryItemOutcomesSummaryProps = {
  className?: string;
  isExpand?: boolean;
  onClickExpand?: (status: string | null) => void;
  statusFilter?: StatusFilter;
  outcomeNb?: number;
  outcomeNbByStatus?: { [OutcomeStatus]: number };
};

const HistoryItemOutcomesSummary = ({
  className,
  isExpand,
  onClickExpand,
  statusFilter,
  outcomeNb,
  outcomeNbByStatus,
}: HistoryItemOutcomesSummaryProps) => {
  const hasOnlyOneFilter = useMemo(
    () => !!outcomeNbByStatus && Object.keys(outcomeNbByStatus).length === 1,
    [outcomeNbByStatus],
  );

  const onClickFilter = useCallback(
    (status) => {
      if (hasOnlyOneFilter && statusFilter !== null) {
        onClickExpand?.(null);
      } else {
        onClickExpand?.(status);
      }
    },
    [onClickExpand, hasOnlyOneFilter, statusFilter],
  );

  return (
    <Wrapper className={className}>
      {outcomeNbByStatus && outcomeNb ? (
        <LineWrapper>
          {STATUS_SUMMARY_ORDER.map((status) =>
            outcomeNbByStatus[status] ? (
              <Line
                key={`outcomes-summary-line-${status.toLowerCase()}`}
                $color={getOutcomeColorFromStatus(status)}
                $width={outcomeNbByStatus[status] / outcomeNb}
              />
            ) : null,
          )}
        </LineWrapper>
      ) : null}
      <OutcomeNbWrapper>
        <ArrowWrapper onClick={() => onClickExpand?.(StatusFilters.ALL)}>
          <StyledExpandArrow $isExpand={isExpand} />
        </ArrowWrapper>
        {outcomeNbByStatus
          ? STATUS_SUMMARY_ORDER.map((status) =>
              outcomeNbByStatus[status] ? (
                <OutcomeNb
                  key={`outcomes-summary-nb-${status.toLowerCase()}`}
                  onClick={() => onClickFilter?.(status)}
                  $selected={statusFilter === status || (hasOnlyOneFilter && statusFilter !== null)}
                >
                  <StatusIcon as={getOutcomeIconFromStatus(status)} />
                  {outcomeNbByStatus[status]}
                </OutcomeNb>
              ) : null,
            )
          : null}
      </OutcomeNbWrapper>
    </Wrapper>
  );
};

export default HistoryItemOutcomesSummary;
